import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useCreateDelivery,
  useDelivery,
  useLastPrices,
  useUpdateDelivery,
} from '../hooks/use-deliveries'
import { useDeliveryRestaurantStore } from '../store/delivery-restaurant-store'
import {
  DeliveryFormHeader,
  DeliveryFormLineRow,
  GoodsValueFooter,
  LINES_CONTAINER,
} from '../components/delivery-line-editor'
import {
  announcedValueCents,
  fillEmptyPrices,
  formatPriceInput,
  parsePriceInput,
} from '../lib/delivery-lines'
import { AffectedOrdersDialog } from '../components/affected-orders-dialog'
import type { FormLine } from '../components/delivery-line-editor'
import type {
  AffectedOrder,
  DeliveryWriteBody,
} from '@/api/endpoints/inventory-deliveries'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import {
  DEFAULT_STOCK_UNIT,
  stockInfoFromFields,
  useStockInfo,
} from '@/features/inventory/hooks/use-stock-info'
import {
  formatStockDisplay,
  parseStockQuantity,
} from '@/shared/lib/stock-quantity'

const buildSchema = (t: (key: string) => string) =>
  z.object({
    supplier: z.string().trim().min(1, t('validation.supplierRequired')),
    deliveryNoteRef: z.string().optional(),
    expectedDate: z.string().min(1, t('validation.expectedDateRequired')),
    countsForPreorders: z.boolean(),
  })

type HeaderForm = z.infer<ReturnType<typeof buildSchema>>

/** A delivery is announced the evening before, so tomorrow is the useful default. */
function tomorrowIso(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

let lineKeySeq = 0
const blankLine = (autoFocus = false): FormLine => ({
  key: `line-${lineKeySeq++}`,
  product: null,
  expectedQty: '',
  note: '',
  price: '',
  autoFocus,
})

export function DeliveryFormPage() {
  const { t } = useTranslation('inventory-deliveries')
  const { t: tCommon } = useTranslation('common')
  const { t: tInventory } = useTranslation('inventory')
  const navigate = useNavigate()
  const { id } = useParams({ strict: false })
  const isEdit = Boolean(id)
  const restaurantId = useDeliveryRestaurantStore((s) => s.restaurantId)

  const { data: existing, isLoading } = useDelivery(
    restaurantId,
    isEdit ? (id as string) : null,
  )
  // Net prices per piece are typed for hand-announced deliveries only (task 9.5); an imported
  // delivery's prices are the Lieferschein's, kept by the server and not shown in this form.
  const showPrices = !isEdit || existing?.source === 'MANUAL'
  const create = useCreateDelivery(restaurantId)
  const update = useUpdateDelivery(restaurantId, id ?? null)

  const [lines, setLines] = useState<Array<FormLine>>([blankLine()])
  const [lineError, setLineError] = useState<string | null>(null)
  const [affected, setAffected] = useState<Array<AffectedOrder> | null>(null)
  // CHG-2026-09-stock-usage-and-price-twins (D9, D11): decimals only for articles with
  // "Teilmengen zählen"; dishes, variants and not-tracked articles cannot be announced.
  // The line's own facts first — a saved line from the server, a product picked in the combobox;
  // only a line without them (a backend before phase B4) is looked up.
  const ownInfo = (l: FormLine) =>
    l.stock ?? (l.product ? stockInfoFromFields(l.product) : null)
  const unknownSkus = useMemo(
    () =>
      lines.flatMap((l) =>
        l.product && !l.stock && stockInfoFromFields(l.product) == null
          ? [l.product.sku]
          : [],
      ),
    [lines],
  )
  const lookedUp = useStockInfo(unknownSkus)
  const infoFor = (l: FormLine) =>
    ownInfo(l) ?? (l.product ? lookedUp.get(l.product.sku) : undefined)

  const form = useForm<HeaderForm>({
    resolver: zodResolver(buildSchema(t)),
    defaultValues: {
      supplier: '',
      deliveryNoteRef: '',
      expectedDate: tomorrowIso(),
      countsForPreorders: true,
    },
  })

  const { reset } = form
  useEffect(() => {
    if (!existing) return
    reset({
      supplier: existing.supplier ?? '',
      deliveryNoteRef: existing.deliveryNoteRef ?? '',
      expectedDate: existing.expectedDate,
      countsForPreorders: existing.countsForPreorders,
    })
    setLines(
      existing.lines.map((l) => ({
        key: `line-${lineKeySeq++}`,
        product: { sku: l.sku, name: l.name ?? l.sku },
        expectedQty: formatStockDisplay(l.expectedQty),
        note: l.note ?? '',
        price:
          existing.source === 'MANUAL' && l.unitCostCents != null
            ? formatPriceInput(
                Math.round(
                  l.unitCostCents / Math.max(1, l.contentPerUnit ?? 1),
                ),
              )
            : '',
        ...(l.fractionalCounting !== undefined ||
        !Number.isInteger(l.expectedQty)
          ? {
              stock: {
                // A saved decimal is never put back into a whole-number field.
                fractional:
                  l.fractionalCounting === true ||
                  !Number.isInteger(l.expectedQty),
                unit: l.unit ?? DEFAULT_STOCK_UNIT,
                noStock: l.noStock ?? null,
              },
            }
          : {}),
      })),
    )
  }, [existing, reset])

  const pending = create.isPending || update.isPending

  function collectLines(): DeliveryWriteBody['lines'] | null {
    const filled = lines.filter((l) => l.product != null)
    if (filled.length === 0) {
      setLineError(t('validation.linesRequired'))
      return null
    }
    const seen = new Set<string>()
    const out: DeliveryWriteBody['lines'] = []
    for (const line of filled) {
      const sku = line.product!.sku
      if (seen.has(sku)) {
        setLineError(t('validation.duplicateSku'))
        return null
      }
      seen.add(sku)
      const info = infoFor(line)
      if (info?.noStock) {
        setLineError(
          tInventory('noStock.lineRefused', { name: line.product!.name }),
        )
        return null
      }
      let qty: number
      if (info?.fractional) {
        const parsed = parseStockQuantity(line.expectedQty)
        if (!parsed.ok) {
          setLineError(t('validation.qtyMinDecimal'))
          return null
        }
        qty = parsed.value
      } else {
        // A whole-number article keeps the rule it always had.
        qty = Number(line.expectedQty.trim())
        if (!Number.isInteger(qty) || qty < 1) {
          setLineError(t('validation.qtyMin'))
          return null
        }
      }
      let unitCostCents: number | null = null
      if (showPrices) {
        const price = parsePriceInput(line.price)
        if (!price.ok) {
          setLineError(t('validation.priceInvalid'))
          return null
        }
        unitCostCents = price.cents
      }
      out.push({
        sku,
        expectedQty: qty,
        note: line.note.trim() === '' ? null : line.note.trim(),
        ...(showPrices ? { unitCostCents } : {}),
      })
    }
    setLineError(null)
    return out
  }

  const onSubmit = form.handleSubmit((values) => {
    const collected = collectLines()
    if (!collected) return

    const body: DeliveryWriteBody = {
      supplier: values.supplier.trim(),
      deliveryNoteRef:
        values.deliveryNoteRef && values.deliveryNoteRef.trim() !== ''
          ? values.deliveryNoteRef.trim()
          : null,
      expectedDate: values.expectedDate,
      countsForPreorders: values.countsForPreorders,
      lines: collected,
    }

    if (isEdit) {
      update.mutate(body, {
        onSuccess: (result) => {
          toast.success(t('toast.updated'))
          // A downward correction can leave pre-orders uncovered; the edit still stands.
          if (result.affectedOrders.length > 0) {
            setAffected(result.affectedOrders)
          } else {
            navigate({
              to: '/inventory/deliveries/$id',
              params: { id: id as string },
            })
          }
        },
        onError: () => toast.error(t('toast.saveFailed')),
      })
      return
    }

    create.mutate(body, {
      onSuccess: (result) => {
        toast.success(t('toast.created'))
        navigate({
          to: '/inventory/deliveries/$id',
          params: { id: result.delivery.id },
        })
      },
      onError: () => toast.error(t('toast.saveFailed')),
    })
  })

  const updateLine = (key: string, next: FormLine) =>
    setLines((prev) => prev.map((l) => (l.key === key ? next : l)))

  /**
   * Enter in a quantity field continues the delivery note instead of submitting: it moves on to
   * the next position, opening its product search. An existing empty line is reused so repeated
   * Enter never piles up blank rows; saving stays an explicit click on "Lieferung speichern".
   */
  const nextLineFrom = (key: string) =>
    setLines((prev) => {
      const current = prev.find((l) => l.key === key)
      if (!current || current.product == null) return prev
      const empty = prev.find((l) => l.product == null)
      if (empty) {
        return prev.map((l) =>
          l.key === empty.key ? { ...l, autoFocus: true } : l,
        )
      }
      return [...prev, blankLine(true)]
    })

  const removeLine = (key: string) =>
    setLines((prev) => {
      const rest = prev.filter((l) => l.key !== key)
      return rest.length > 0 ? rest : [blankLine()]
    })

  const lastPrices = useLastPrices(
    showPrices ? restaurantId : null,
    lines.flatMap((l) => (l.product ? [l.product.sku] : [])),
  )
  const fillLastPrices = () =>
    setLines((prev) =>
      fillEmptyPrices(prev, (l) =>
        l.product ? lastPrices.get(l.product.sku)?.unitCostCents : undefined,
      ),
    )
  const pricedValue = showPrices
    ? announcedValueCents(
        lines
          .filter((l) => l.product != null)
          .map((l) => ({ qty: l.expectedQty, price: l.price })),
      )
    : null

  const totalUnits = lines.reduce((sum, l) => {
    const qty = Number(l.expectedQty)
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0)
  }, 0)

  return (
    <PermissionGate action={ACTIONS.INVENTORY_DELIVERY}>
      <div className="flex flex-col gap-4 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">
            {isEdit ? t('form.titleEdit') : t('form.titleNew')}
          </h1>
          <Button variant="outline" asChild>
            <Link to="/inventory/deliveries">{t('detail.back')}</Link>
          </Button>
        </div>

        {isEdit && isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {isEdit && !isLoading && !existing && (
          <p className="text-sm text-muted-foreground">{t('form.notFound')}</p>
        )}

        {isEdit && existing && existing.status !== 'ANNOUNCED' && (
          <p className="text-sm text-destructive">
            {t('form.onlyAnnouncedEditable')}
          </p>
        )}

        {(!isEdit || (existing && existing.status === 'ANNOUNCED')) && (
          <form
            onSubmit={onSubmit}
            // Enter anywhere in the form types on, it never saves: announcing a delivery is a
            // rapid product/quantity loop, and an accidental submit halfway through would announce
            // a half-typed document. Saving is the explicit button (still reachable by keyboard:
            // Tab to it and press Enter, because then the event target is the button itself).
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                (e.target as HTMLElement).tagName === 'INPUT'
              ) {
                e.preventDefault()
              }
            }}
            className="flex flex-col gap-4"
          >
            <Card>
              <CardHeader>
                <CardTitle>{t('detail.title')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="supplier">{t('form.supplier')}</Label>
                    <Input
                      id="supplier"
                      placeholder={t('form.supplierPlaceholder')}
                      {...form.register('supplier')}
                    />
                    {form.formState.errors.supplier && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.supplier.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="deliveryNoteRef">
                      {t('form.deliveryNoteRef')}
                    </Label>
                    <Input
                      id="deliveryNoteRef"
                      {...form.register('deliveryNoteRef')}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="expectedDate">
                      {t('form.expectedDate')}
                    </Label>
                    <Input
                      id="expectedDate"
                      type="date"
                      {...form.register('expectedDate')}
                    />
                    {form.formState.errors.expectedDate && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.expectedDate.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="countsForPreorders"
                      checked={form.watch('countsForPreorders')}
                      onCheckedChange={(v) =>
                        form.setValue('countsForPreorders', v)
                      }
                    />
                    <Label htmlFor="countsForPreorders">
                      {t('form.countsForPreorders')}
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('form.countsForPreordersHint')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('form.lines')}</CardTitle>
                {showPrices && (
                  <CardAction>
                    {/* Fills only the empty price fields; a typed price is never overwritten. */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={lastPrices.size === 0}
                      onClick={fillLastPrices}
                    >
                      {t('price.copyAll')}
                    </Button>
                  </CardAction>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <div className={LINES_CONTAINER}>
                  <DeliveryFormHeader showPrices={showPrices} />
                  {lines.map((line) => (
                    <DeliveryFormLineRow
                      onQuantityEnter={() => nextLineFrom(line.key)}
                      key={line.key}
                      line={line}
                      stock={infoFor(line) ?? undefined}
                      restaurantId={restaurantId ?? undefined}
                      showPrices={showPrices}
                      lastPrice={
                        line.product
                          ? lastPrices.get(line.product.sku)
                          : undefined
                      }
                      onChange={(next) => updateLine(line.key, next)}
                      onRemove={() => removeLine(line.key)}
                    />
                  ))}
                </div>
                {pricedValue != null && (
                  <GoodsValueFooter
                    label={t('price.goodsValuePriced')}
                    cents={pricedValue}
                  />
                )}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLines((prev) => [...prev, blankLine()])}
                  >
                    {t('form.addLine')}
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    {t('form.summary', {
                      lines: lines.filter((l) => l.product != null).length,
                      units: totalUnits,
                    })}
                  </p>
                </div>
                {lineError && (
                  <p className="border-t p-3 text-sm text-destructive">
                    {lineError}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-end gap-3 border-t bg-background px-4 py-3 sm:mx-0 sm:rounded-md sm:border">
              <Button variant="outline" asChild>
                <Link to="/inventory/deliveries">{tCommon('cancel')}</Link>
              </Button>
              <Button type="submit" disabled={pending}>
                {t('form.save')}
              </Button>
            </div>
          </form>
        )}

        <AffectedOrdersDialog
          orders={affected}
          onClose={() => {
            setAffected(null)
            if (isEdit) {
              navigate({
                to: '/inventory/deliveries/$id',
                params: { id: id as string },
              })
            }
          }}
        />
      </div>
    </PermissionGate>
  )
}
